/**
 * Supported locales — Phase 6 Pass 2, iter 237.
 *
 * Per `pass-2-six-language-pack.md` § "Files to create →
 * packages/i18n/src/locales.ts: supported list + defaults".
 *
 * **Architect hat decision (iter 237, locked)**: i18n primitives live
 * in `packages/shared/src/i18n/` for v1 (alongside iter-235
 * `pseudoLocalize`). Pass-2 spec suggested a new `packages/i18n`
 * package, but until the i18n directory grows past ~6 files +
 * accumulates dep-management work (per the 3-consumer rule in
 * `.claude/rules/architecture.md` § "When to Reach for a New
 * Package"), keeping these primitives in `shared` avoids workspace
 * proliferation. v1.1 lifts to `packages/i18n` when the `messages/`
 * JSON files land + the consumer count justifies it.
 *
 * **Locked v1 locale set** (Product hat, locked, Pass-2 spec § "EN +
 * ES + FR + DE + AR + HE"):
 *
 *   - `'en'` — English (baseline, BCP 47 primary subtag)
 *   - `'es'` — Spanish
 *   - `'fr'` — French
 *   - `'de'` — German
 *   - `'ar'` — Arabic (RTL)
 *   - `'he'` — Hebrew (RTL)
 *
 * **Per-locale direction metadata** (Designer hat, locked, Pass-1
 * iter-231 Bdi + Phase-0 ESLint RTL rule precedent): each locale
 * carries an explicit `dir` field. Components + the `<html dir>`
 * driver read this rather than hard-coding "Arabic + Hebrew = RTL"
 * heuristics. Future v1.1 locales (Farsi, Urdu) get one-line
 * additions here.
 *
 * **`pseudo` locale absent from `SUPPORTED_LOCALES`** (QA hat, locked,
 * iter-235 precedent): the pseudo-localization debug locale (iter-235)
 * is dev/CI-only — not a real-language user-facing choice. It's
 * accepted as a side-channel via env / dev URL param at runtime;
 * doesn't appear in the public locale picker. Per Pass-1 iter-235
 * "DEV-TIME debugging mode, distinct from a real translation".
 *
 * **Default locale = English** (Product hat, locked, Pass-2 spec
 * § "EN baseline; ~500 strings"): English is the source-of-truth
 * for translation extraction. Any locale fallback chain terminates
 * at EN. The auto-detection chain (iter-238 `detect.ts`) returns EN
 * if no Accept-Language match is found.
 *
 * **Pure data + 1 helper** (Architect hat, locked, iter-235 pattern):
 * `SUPPORTED_LOCALES` is a readonly tuple; `getLocaleDirection`
 * resolves a locale code to `'ltr' | 'rtl'`. Trivially testable;
 * no I/O.
 */

import { z } from 'zod';

export type Locale = 'en' | 'es' | 'fr' | 'de' | 'ar' | 'he';

export type LocaleDirection = 'ltr' | 'rtl';

export interface LocaleMetadata {
  readonly code: Locale;
  readonly displayName: string;
  readonly nativeName: string;
  readonly dir: LocaleDirection;
}

/**
 * The locked v1 launch set. Order is the order the locale picker
 * displays (English first as the default; LTR Latin languages next;
 * RTL last). Native-speaker review per language gated on BL-014.
 */
export const SUPPORTED_LOCALES: readonly LocaleMetadata[] = [
  { code: 'en', displayName: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'es', displayName: 'Spanish', nativeName: 'Español', dir: 'ltr' },
  { code: 'fr', displayName: 'French', nativeName: 'Français', dir: 'ltr' },
  { code: 'de', displayName: 'German', nativeName: 'Deutsch', dir: 'ltr' },
  { code: 'ar', displayName: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'he', displayName: 'Hebrew', nativeName: 'עברית', dir: 'rtl' },
];

/** Default locale — the baseline every fallback chain terminates at. */
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * The supported locale codes as a literal tuple — the single source of truth
 * any Zod schema that keys data by locale reuses (see {@link LocaleCodeSchema}).
 * Listed literally (not `SUPPORTED_LOCALES.map(...)`) so `z.enum` receives a
 * non-empty tuple literal and the inferred type is exactly the `Locale` union.
 * The two compile-time guards below make it a type error to list a non-locale
 * (subset) OR to omit a locale (superset), so it can never silently drift from
 * `Locale`; the `locales.test.ts` "same 6 codes" test also locks it to
 * `SUPPORTED_LOCALES`' order.
 */
export const LOCALE_CODES = ['en', 'es', 'fr', 'de', 'ar', 'he'] as const;

type _CodesAreLocales = (typeof LOCALE_CODES)[number] extends Locale ? true : never;
type _LocalesAreCoded = Locale extends (typeof LOCALE_CODES)[number] ? true : never;
const _localeCodeCoverage: [_CodesAreLocales, _LocalesAreCoded] = [true, true];
void _localeCodeCoverage;

/**
 * Zod enum of the supported locale codes — reuse this wherever a schema keys
 * data by locale (e.g. CS-101d capability localization) so the 6 codes are
 * never re-hardcoded downstream. Rejects any code outside the launch set.
 */
export const LocaleCodeSchema = z.enum(LOCALE_CODES);

/**
 * Type-guard checking whether a candidate string is one of the
 * supported v1 locale codes.
 */
export function isSupportedLocale(candidate: string): candidate is Locale {
  return (LOCALE_CODES as readonly string[]).includes(candidate);
}

/**
 * Resolve the writing direction for a locale. Unknown locales fall
 * back to `'ltr'` (the safe default for arbitrary text).
 */
export function getLocaleDirection(locale: string): LocaleDirection {
  const found = SUPPORTED_LOCALES.find((l) => l.code === locale);
  return found?.dir ?? 'ltr';
}

/**
 * Resolve full metadata for a locale. Returns `undefined` when the
 * code is unknown.
 */
export function getLocaleMetadata(locale: string): LocaleMetadata | undefined {
  return SUPPORTED_LOCALES.find((l) => l.code === locale);
}

/**
 * The Arabic numeral system a surface renders digits in. Per the locked
 * spec (`product/trust/localization-rtl.md` § Numerals), Arabic UI
 * defaults to **Western** digits (0-9) — what UAE/KSA/Egypt use
 * day-to-day — with **Eastern** Arabic-Indic (٠-٩) as an explicit
 * opt-in (a per-user preference). Hebrew and every other locale always
 * use Western.
 */
export type ArabicNumerals = 'western' | 'eastern';

/**
 * The Unicode numbering system whose digits a locale renders in.
 *
 * Pinned explicitly rather than relying on `Intl`'s per-locale default,
 * because the default numbering system for `ar` varies by ICU build:
 * some Node/ICU builds resolve `ar` to `latn`, others to `arab`. Pinning
 * makes the rendered digits deterministic on any ICU build.
 *
 * Per the locked spec, the default is **Western** (`latn`) for every
 * locale including Arabic; `arab` (Eastern Arabic-Indic) is returned
 * ONLY for Arabic when the caller opts in via `numerals: 'eastern'`
 * (the user preference). Hebrew and unknown locales are always `latn`.
 *
 * Callers that format a user-visible quantity feed the result into
 * `numberingSystem` so the digits never depend on the host ICU defaults.
 */
export function getLocaleNumberingSystem(
  locale: string,
  numerals: ArabicNumerals = 'western',
): string {
  const isArabic = locale === 'ar' || locale.startsWith('ar-');
  return isArabic && numerals === 'eastern' ? 'arab' : 'latn';
}
